import { useCallback } from 'react';
import type { GridColDef, GridColumnResizeParams } from '@mui/x-data-grid-pro';
import { useSessionState } from './useSessionState';
import { getLocalStorageJSON, setLocalStorageJSON, removeLocalStorageKeys } from '../utils/HelperFunction';

export type GridColumnWidthMap = Record<string, number>;
const DEFAULT_COLUMN_WIDTH = 180;
const DEFAULT_COLUMN_MIN_WIDTH = 140;

/**
 * Persists DataGrid column widths for the current browser session.
 */
export function usePersistedGridState(gridKey: string) {
    const [columnWidths, setColumnWidths] = useSessionState<GridColumnWidthMap>(
        `grid:${gridKey}:columnWidths`,
        {},
    );

    const onColumnWidthChange = useCallback((params: GridColumnResizeParams) => {
        const field = params.colDef?.field;
        const width = params.width;

        if (!field || !Number.isFinite(width)) return;

        setColumnWidths((prev) => {
            if (prev[field] === width) return prev;
            return { ...prev, [field]: width };
        });
    }, [setColumnWidths]);

    const clearColumnWidths = useCallback(() => {
        setColumnWidths({});
    }, [setColumnWidths]);

    const applyColumnWidths = useCallback((columns: GridColDef[]): GridColDef[] => {
        if (!Array.isArray(columns)) return columns;

        const hasSavedWidths = Object.keys(columnWidths).length > 0;

        return columns.map((col) => {
            const savedWidth = columnWidths[col.field];
            if (Number.isFinite(savedWidth)) {
                // Freeze resized columns with explicit width so future column ops don't auto-reflow them.
                return { ...col, width: savedWidth, flex: undefined };
            }

            if (!hasSavedWidths) {
                // Initial/default rendering: stretch unsized columns to occupy available viewport width.
                const hasExplicitWidth = Number.isFinite(col.width as number);
                const hasFlex = typeof col.flex === 'number';
                if (hasExplicitWidth || hasFlex) return col;
                return { ...col, flex: 1, minWidth: col.minWidth ?? DEFAULT_COLUMN_MIN_WIDTH };
            }

            // After any manual resize in the current session, avoid auto-width behavior by
            // turning remaining non-persisted columns into fixed defaults.
            const hasExplicitWidth = Number.isFinite(col.width as number);
            if (hasExplicitWidth) return col;

            return {
                ...col,
                width: Number.isFinite(col.minWidth as number) ? (col.minWidth as number) : DEFAULT_COLUMN_WIDTH,
                flex: undefined,
            };
        });
    }, [columnWidths]);

    return {
        columnWidths,
        onColumnWidthChange,
        clearColumnWidths,
        applyColumnWidths,
    };
}

interface UseFilterPanelPersistenceOptions {
    storagePrefix: string;
    persistState: boolean;
    showArchived: boolean;
    activeTab?: string;
}

/**
 * Encapsulates GenericFilterPanel localStorage persistence operations.
 */
export function useFilterPanelPersistence({
    storagePrefix,
    persistState,
    showArchived,
    activeTab,
}: UseFilterPanelPersistenceOptions) {
    const getStorageKey = useCallback((suffix: 'activeFilter' | 'formData') => {
        const tabPart = activeTab ? `${activeTab}_` : '';
        return `${storagePrefix}_${tabPart}${suffix}`;
    }, [storagePrefix, activeTab]);

    const loadFromStorage = useCallback(() => {
        if (!persistState) return null;

        const savedActiveFilter = getLocalStorageJSON(getStorageKey('activeFilter'), null);
        if (!savedActiveFilter) return null;

        const savedFormData = getLocalStorageJSON(getStorageKey('formData'), null) as Record<string, any> | null;

        return {
            savedActiveFilter,
            savedFormData,
        };
    }, [persistState, getStorageKey]);

    const saveActiveFilter = useCallback((activeFilter: any) => {
        if (!persistState || showArchived) return;
        setLocalStorageJSON(getStorageKey('activeFilter'), activeFilter);
    }, [persistState, showArchived, getStorageKey]);

    const saveFormData = useCallback((formData: Record<string, any>) => {
        if (!persistState || showArchived || Object.keys(formData).length === 0) return;
        setLocalStorageJSON(getStorageKey('formData'), formData);
    }, [persistState, showArchived, getStorageKey]);

    const clear = useCallback(() => {
        if (!persistState) return;
        removeLocalStorageKeys(getStorageKey('formData'), getStorageKey('activeFilter'));
    }, [persistState, getStorageKey]);

    return {
        loadFromStorage,
        saveActiveFilter,
        saveFormData,
        clear,
    };
}
